import { DataGridUtils } from 'src/shared/utils/DataGridUtils'
import { Project_Base, Project_Base_Bool_Exp, Project_Financial_Report, Query_Root } from 'src/@types/graphql'
import { PROJECT } from 'src/shared/constants/constants'
import combinedQuery from 'graphql-combine-query'
import { queryProjectBudgetByProjectBase } from 'src/screens/shared/project/details/financials/projectFinancialsQueries'
import {
  queryProjectFinancialReportByMilestoneId,
  queryProjectFinancialReportsByProject,
} from 'src/screens/shared/implementation/details/milestoneQueries'
import { sumBy } from 'lodash'
import { Client } from 'urql'

export interface MilestoneProjectFundsActuals {
  gfch_share_annual_value?: number | null
  share_responsible_organization_annual_value?: number | null
  other_funding_annual_value?: number | null
}

export const calculateActualsProjectFundsPercentage = (
  totalActualsData: MilestoneProjectFundsActuals | null | undefined,
  currentActual: number,
): number | null => {
  if (totalActualsData) {
    const totalValue =
      (totalActualsData?.gfch_share_annual_value ?? 0) +
      (totalActualsData?.share_responsible_organization_annual_value ?? 0) +
      (totalActualsData?.other_funding_annual_value ?? 0)

    const value = (currentActual / totalValue) * 100
    const parsedValue = parseInt(value.toString())

    const precision = value === parsedValue ? 0 : 1
    const multiplier = Math.pow(10, precision)

    const result = Math.round(value * multiplier) / multiplier

    return DataGridUtils.formatFieldValue(result)
  }
  return 0
}

export interface FetchedTotalActualsData {
  gfch_share_annual_value: number
  share_responsible_organization_annual_value: number
  other_funding_annual_value: number
}

export interface FetchedProjectFundsData {
  fetchedProjectBudgetData?: Project_Base
  fetchedFinancialReportData?: Project_Financial_Report
  fetchedTotalActualsData?: FetchedTotalActualsData
}

export const fetchProjectFundsData =
  (urqlClient: Client) =>
  async (project_id: number, milestone_id: number, process: string): Promise<FetchedProjectFundsData> => {
    const pfKapQuery = {
      pf_kap_projects: {
        id: {
          _eq: project_id,
        },
      },
    }

    const pfPgvQuery = {
      pf_pgv_projects: {
        id: {
          _eq: project_id,
        },
      },
    }

    const projectBaseQuery: Project_Base_Bool_Exp = {
      ...(process === PROJECT.PF_KAP && pfKapQuery),
      ...(process === PROJECT.PF_PGV && pfPgvQuery),
    }

    const { document, variables } = combinedQuery('projectBudgetAndFinancialReport')
      .add<
        {
          project_base: Query_Root['project_base']
        },
        { projectBaseQuery: Project_Base_Bool_Exp }
      >(queryProjectBudgetByProjectBase, {
        projectBaseQuery: projectBaseQuery,
      })
      .add<
        {
          project_financial_report: Query_Root['project_financial_report']
        },
        {
          milestoneId: number
        }
      >(queryProjectFinancialReportByMilestoneId, { milestoneId: milestone_id })
      .add<
        {
          project_financial_all_reports: Query_Root['project_financial_report']
        },
        {
          projectBaseWhere: Project_Base_Bool_Exp
        }
      >(queryProjectFinancialReportsByProject, { projectBaseWhere: projectBaseQuery })

    const { data, error } = await urqlClient
      .query<
        {
          project_base: Query_Root['project_base']
          project_financial_report: Query_Root['project_financial_report']
          project_financial_all_reports: Query_Root['project_financial_report']
        },
        { projectBaseQuery: Project_Base_Bool_Exp; milestoneId: number; projectBaseWhere: Project_Base_Bool_Exp }
      >(document, variables!)
      .toPromise()

    if (error || data === undefined) {
      throw error
    } else {
      const allMilestonesFinancialReports = data.project_financial_all_reports
      const milestonesFinancialReportsActuals = {
        gfch_share_annual_value: sumBy(allMilestonesFinancialReports, 'gfch_share_annual_value'),
        share_responsible_organization_annual_value: sumBy(
          allMilestonesFinancialReports,
          'share_responsible_organization_annual_value',
        ),
        other_funding_annual_value: sumBy(allMilestonesFinancialReports, 'other_funding_annual_value'),
      }

      return {
        fetchedProjectBudgetData: data.project_base[0],
        fetchedFinancialReportData: data.project_financial_report[0],
        fetchedTotalActualsData: milestonesFinancialReportsActuals,
      }
    }
  }
